<script setup>
import { ref } from "vue";
import { useLanding } from "../../composables/useLanding";
import { usePauseOffscreen } from "../../composables/usePauseOffscreen";
import { useScrollReveal } from "../../composables/useScrollReveal";
import { motion, useReducedMotion } from "motion-v";

const { label, customers } = useLanding().logos;

const marquee = ref(null);
const root = ref(null);
const { paused } = usePauseOffscreen(marquee);
const { revealed } = useScrollReveal(root);

// The grid's logos spring up one after another, once, and lift on hover.
const reduced = useReducedMotion();
const hidden = { opacity: 0, y: 18, scale: 0.85 };
const shown = { opacity: 1, y: 0, scale: 1 };
</script>

<template>
  <section
    ref="root"
    class="logos reveal reveal--fade section--tight"
    :class="{ 'is-revealed': revealed }"
  >
    <div class="container">
      <p class="logos__label">{{ label }}</p>
    </div>

    <!-- Static grid on wide screens; a marquee where the row will not fit. -->
    <div class="logos__grid container">
      <motion.figure
        v-for="(c, i) in customers"
        :key="c.name"
        class="logos__item"
        :initial="reduced ? false : hidden"
        :while-in-view="shown"
        :in-view-options="{ once: true, amount: 0.6 }"
        :while-hover="reduced ? undefined : { y: -4, scale: 1.06, transition: { delay: 0 } }"
        :transition="{ type: 'spring', stiffness: 260, damping: 20, delay: i * 0.06 }"
      >
        <img :src="c.logo" :alt="c.name" width="72" height="72" loading="lazy" />
        <figcaption>{{ c.name }}</figcaption>
      </motion.figure>
    </div>

    <div
      ref="marquee"
      class="logos__marquee marquee"
      :class="{ 'is-paused': paused }"
    >
      <div class="marquee__track">
        <!-- Duplicated so the -50% translate loops seamlessly. -->
        <figure
          v-for="(c, i) in [...customers, ...customers]"
          :key="`${c.name}-${i}`"
          class="logos__item"
          :aria-hidden="i >= customers.length ? 'true' : undefined"
        >
          <img :src="c.logo" :alt="i >= customers.length ? '' : c.name" width="64" height="64" loading="lazy" />
          <figcaption>{{ c.name }}</figcaption>
        </figure>
      </div>
    </div>
  </section>
</template>

<style scoped>
.logos {
  background: var(--surface);
  border-block: var(--border);
}

.logos__label {
  text-align: center;
  font-size: var(--text-sm);
  font-weight: var(--weight-semibold);
  letter-spacing: var(--tracking-wide);
  color: var(--ink-subtle);
  margin-block-end: var(--space-6);
}

.logos__item {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--space-2);
  margin: 0;
  padding-inline: var(--space-4);
  flex-shrink: 0;
}

.logos__item img {
  inline-size: 64px;
  block-size: 64px;
  border-radius: 50%;
  object-fit: cover;
  transition: transform var(--dur-base) var(--ease-out),
    box-shadow var(--dur-base) var(--ease-out);
}

.logos__item:hover img {
  transform: scale(1.06);
  box-shadow: var(--lift-2);
}

.logos__item figcaption {
  font-size: var(--text-xs);
  color: var(--ink-subtle);
  text-align: center;
  white-space: nowrap;
}

.logos__grid {
  display: none;
  flex-wrap: wrap;
  justify-content: center;
  gap: var(--space-5);
}

.logos__marquee {
  display: block;
}

@media (min-width: 900px) {
  .logos__grid {
    display: flex;
  }

  .logos__marquee {
    display: none;
  }
}
</style>
